import React, { FC } from "react";

import { ChakraProvider } from "@chakra-ui/react";
import { appWithTranslation } from "next-i18next";
import type { AppProps } from "next/app";

import ErrorBoundary from "@components/ErrorBoundary";
import { MessageContextProvider } from "@components/Message";
import { BrowserFeatureCheckProvider } from "@lib/browser-version-message";
import { appWithLocales } from "@lib/custom-locales";
import Fonts from "@lib/fonts";
import { Desktop, MediaContextProvider } from "@lib/media";
import theme from "@lib/theme";
import { AuthProvider } from "@modules/auth";
import FeatureProvider from "@modules/features/FeatureProvider";
import { createLayout } from "@modules/layout";
import { useResetStoredOrder } from "@modules/order";

import i18nConfig from "../next-i18next.config";

import "../public/assets/css/chatbot.css";
import UserFeedbackForm from "@modules/userFeedback/components/UserFeedbackForm";
import { RecaptchaProvider } from "@lib/recaptcha";
import { useRouter } from "next/router";
import { FEEDBACK_FORM } from "@modules/features/constants";
import getGoogleClientId from "@modules/login/utils/getGoogleClientId";
import { GoogleOAuthProvider } from '@react-oauth/google';

const App: FC<AppProps> = ({ Component, pageProps: { hasAuthCookie, features, ...pageProps } }: AppProps) => {
  const Layout = createLayout(Component, pageProps.defaultLocale);
  const feedbackFormEnabled = features?.includes(FEEDBACK_FORM);
  const router = useRouter();
  const excludedRoutes = ["/login", "/register", "/password/reset"];
  const isExcludedRoute = excludedRoutes.includes(router.pathname);
  const clientId = getGoogleClientId(pageProps.defaultLocale);

  useResetStoredOrder();
  return (
    <ErrorBoundary>
      <GoogleOAuthProvider clientId={clientId || ''}>
      <ChakraProvider theme={theme}>
        <Fonts />
        <MediaContextProvider>
          <AuthProvider hasAuthCookie={hasAuthCookie}>
            <FeatureProvider features={features}>
              <MessageContextProvider>
                <BrowserFeatureCheckProvider>
                  <Layout>
                    <>
                      <Component {...pageProps} />
                      {!isExcludedRoute && feedbackFormEnabled && (
                        <Desktop>
                          <RecaptchaProvider isEnabled domain={pageProps.defaultLocale}>
                            <UserFeedbackForm />
                          </RecaptchaProvider>
                        </Desktop>
                      )}
                    </>
                  </Layout>
                </BrowserFeatureCheckProvider>
              </MessageContextProvider>
            </FeatureProvider>
          </AuthProvider>
        </MediaContextProvider>
      </ChakraProvider>
      </GoogleOAuthProvider>
    </ErrorBoundary>
  );
};
export default appWithTranslation(appWithLocales(App), i18nConfig as any);
